import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, Clock, GraduationCap, Briefcase, FileText, Download } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import { getImageUrl, getFileUrl } from '../lib/utils';

const ProgramDetail = () => {
  const { id } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/programs/${id}`)
      .then(res => res.json())
      .then(data => setProgram(data))
      .catch(err => console.error('Error fetching program:', err))
      .finally(() => setLoading(false));
  }, [id, BACKEND_URL]);

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
      return url;
    }
    return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // دالة لاستخراج معرف الفيديو من رابط YouTube
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">البرنامج غير موجود</h2>
          <Link to="/programs">
            <Button className="bg-[#0F388E]">
              <ArrowRight className="ml-2" />
              العودة إلى البرامج
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {program && (
        <>
          <SEO 
            title={program.title_ar}
            description={program.description_ar || program.about_ar || `برنامج ${program.title_ar} - ${program.degree} - ${program.duration}`}
            keywords={`${program.title_ar}, ${program.degree}, ${program.title_en || ''}, جامعة ابن النفيس`}
            image={program.image}
            url={`https://uni-content-fixer.preview.emergentagent.com/programs/${program.id}`}
            type="article"
          />
          
          <StructuredData 
            type="course"
            data={program}
          />
          
          <StructuredData 
            type="breadcrumb"
            data={{
              items: [
                { name: 'الرئيسية', url: '/' },
                { name: 'البرامج الأكاديمية', url: '/programs' },
                { name: program.title_ar, url: `/programs/${program.id}` }
              ]
            }}
          />
        </>
      )}
      
      {/* Hero Section */}
      <section className="relative pt-36 sm:pt-40 pb-20 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] text-white" style={{ paddingTop: '140px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-white/80">
              <li>
                <Link to="/" className="hover:text-white transition-colors">الرئيسية</Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/programs" className="hover:text-white transition-colors">البرامج الأكاديمية</Link>
              </li>
              <li>/</li>
              <li className="text-[#C9A040] font-medium">{program.title_ar}</li>
            </ol>
          </nav>
          <Link to="/programs" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowRight className="ml-2 w-5 h-5" />
            العودة إلى البرامج
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{program.title_ar}</h1>
              <p className="text-xl text-white/90 mb-6">{program.description_ar}</p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Clock className="w-5 h-5" />
                  <span>{program.duration}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <GraduationCap className="w-5 h-5" />
                  <span>{program.degree}</span>
                </div>
              </div>
            </div>
            {program.image && (
              <div className="md:col-span-1">
                <img
                  src={getImageUrl(program.image)}
                  alt={program.title_ar}
                  className="w-full h-64 object-cover rounded-lg shadow-2xl"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      {program.about_ar && (
        <section className="py-16 bg-white dark:bg-[#071326]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white mb-6">نبذة عن البرنامج</h2>
            <div className="w-24 h-1 bg-[#C9A040] mb-8"></div>
            <div className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {program.about_ar}
            </div>
          </div>
        </section>
      )}

      {/* Video Section */}
      {program.video_url && getYouTubeVideoId(program.video_url) && (
        <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-[#071326] dark:to-[#0B1A30]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white mb-4">شاهد فيديو عن البرنامج</h2>
              <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
            </div>
            <div className="max-w-5xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(program.video_url)}?rel=0&modestbranding=1`}
                  title={program.title_ar}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Vision, Mission, Objectives */}
      {(program.vision_ar || program.mission_ar || program.objectives_ar) && (
        <section className="py-16 bg-gray-50 dark:bg-[#0B1A30]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {program.vision_ar && (
                <Card className="hover:shadow-xl transition-shadow dark:bg-[#0E2243] dark:border-gray-800">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-4">الرؤية</h3>
                    <div className="w-16 h-1 bg-[#C9A040] mb-4"></div>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{program.vision_ar}</p>
                  </CardContent>
                </Card>
              )}

              {program.mission_ar && (
                <Card className="hover:shadow-xl transition-shadow dark:bg-[#0E2243] dark:border-gray-800">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-4">الرسالة</h3>
                    <div className="w-16 h-1 bg-[#C9A040] mb-4"></div>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{program.mission_ar}</p>
                  </CardContent>
                </Card>
              )}

              {program.objectives_ar && (
                <Card className="hover:shadow-xl transition-shadow dark:bg-[#0E2243] dark:border-gray-800">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-4">الأهداف</h3>
                    <div className="w-16 h-1 bg-[#C9A040] mb-4"></div>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{program.objectives_ar}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Career Fields */}
      {program.career_fields_ar && (
        <section className="py-16 bg-white dark:bg-[#071326]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Briefcase className="w-8 h-8 text-[#C9A040]" />
              <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white">مجالات العمل بعد التخرج</h2>
            </div>
            <div className="w-24 h-1 bg-[#C9A040] mb-8"></div>
            <div className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {program.career_fields_ar}
            </div>
          </div>
        </section>
      )}

      {/* Study Plan Section */}
      {(program.study_plan_ar || program.study_plan_pdf || program.learning_outcomes_file) && (
        <section className="py-16 bg-gray-50 dark:bg-[#0B1A30]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-8 h-8 text-[#C9A040]" />
              <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white">الخطة الدراسية</h2>
            </div>
            <div className="w-24 h-1 bg-[#C9A040] mb-8"></div>
            
            {program.study_plan_ar && (
              <div className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line mb-6">
                {program.study_plan_ar}
              </div>
            )}

            {program.study_plan_pdf && (
              <a
                href={getFileUrl(program.study_plan_pdf)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0F388E] hover:bg-[#123985] text-white px-6 py-3 rounded-lg transition-colors mb-6 shadow-md"
              >
                <Download className="w-5 h-5" />
                تحميل الخطة الدراسية (PDF)
              </a>
            )}

            {/* بطاقة مخرجات التعلم */}
            {program.learning_outcomes_file && (
              <Card className="mt-6 border-2 border-[#C9A040] dark:border-[#C9A040] bg-gradient-to-r from-amber-50 to-orange-50 dark:from-[#0E2243] dark:to-[#0A1A33] shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-[#C9A040] p-3 rounded-full">
                        <GraduationCap className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#0F388E] dark:text-white mb-1">
                          مخرجات التعلم
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Learning Outcomes Document
                        </p>
                      </div>
                    </div>
                    <a
                      href={getFileUrl(program.learning_outcomes_file)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-[#0F388E] hover:bg-[#123985] text-white px-6 py-3 rounded-lg transition-all hover:scale-105 shadow-md"
                    >
                      <Download className="w-5 h-5" />
                      <span className="font-semibold">تحميل الملف</span>
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Course Schedules - الجداول الدراسية */}
      {program.course_schedules && program.course_schedules.length > 0 && (
        <section className="py-16 bg-white dark:bg-[#071326]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-8 h-8 text-[#C9A040]" />
              <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white">الجداول الدراسية</h2>
            </div>
            <div className="w-24 h-1 bg-[#C9A040] mb-8"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {program.course_schedules.map((schedule, index) => (
                <Card key={schedule.id || index} className="group hover:shadow-xl transition-all duration-300 border-2 border-purple-200 dark:border-gray-800 hover:border-[#C9A040] dark:hover:border-[#C9A040] bg-gradient-to-br from-purple-50 to-pink-50 dark:from-[#0E2243] dark:to-[#0A1A33]">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#0F388E] p-3 rounded-full flex-shrink-0">
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-[#0F388E] dark:text-white mb-1">
                          {schedule.name_ar}
                        </h3>
                        {schedule.name_en && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                            {schedule.name_en}
                          </p>
                        )}
                        <div className="inline-block bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold mb-3">
                          {schedule.level}
                        </div>
                        <a
                          href={getFileUrl(schedule.file_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-[#0F388E] dark:text-[#C9A040] font-semibold hover:underline transition-all hover:gap-3"
                        >
                          <Download className="w-5 h-5" />
                          <span>تحميل الجدول الدراسي</span>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Requirements */}
      {(program.requirements_ar || program.admission_requirements_file || program.admission_file) && (
        <section className="py-16 bg-white dark:bg-[#071326]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white mb-6">شروط القبول</h2>
            <div className="w-24 h-1 bg-[#C9A040] mb-8"></div>
            {program.requirements_ar && (
              <div className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                {program.requirements_ar}
              </div>
            )}
            {(program.admission_requirements_file || program.admission_file) && (
              <div className="mt-8">
                <a
                  href={getFileUrl(program.admission_requirements_file || program.admission_file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#0F388E] hover:bg-[#123985] text-white px-6 py-3.5 rounded-xl transition-all shadow-md hover:scale-105"
                >
                  <Download className="w-5 h-5 text-[#C9A040]" />
                  <span className="font-bold">تحميل ملف وثائق وشروط القبول (PDF)</span>
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">هل أنت مهتم بهذا البرنامج؟</h2>
          <p className="text-xl mb-8">انضم إلينا وابدأ رحلتك الأكاديمية</p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#C9A040] hover:bg-[#C9A040]/90 text-white">
              تواصل معنا للتسجيل
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetail;
